// Station classes — a venue's price and permission tiers (F-ORG-03; spec/13 §12, spec/04 §3).
//
//   GET   /v1/organizations/{o}/venues/{v}/station-classes              the venue's classes   stations:read
//   POST  /v1/organizations/{o}/venues/{v}/station-classes              create one            stations:manage
//   PATCH /v1/organizations/{o}/venues/{v}/station-classes/{classId}    edit one              stations:manage
//
// Standard, Premium, Console, VR. Passes, rate cards and reservations name a **class**, never a
// single station, so a class is the unit a venue prices and sells by. A station belongs to exactly
// one (F-ORG-03 rule 3; the station half arrives with the `station` table, F-STN-02).
//
// A class is also a scope in the settings chain (spec/03 §9: station → station class → zone →
// venue → organisation → platform), so `sessions.offlinePolicy` or `cleanup.profile` can differ by
// class. Its id is what a class-scoped setting is stored against.
import { createRoute } from '@hono/zod-openapi';
import { z } from "../zod.js";
import { OrganizationIdSchema, StationClassIdSchema, VenueIdSchema } from "../ids/schema.js";
import { ColorTokenSchema } from "../primitives/color-token.js";
import { PageQuerySchema, paginated } from "../primitives/page.js";
import { IsoDateTimeSchema } from "../primitives/scalars.js";
import { VenueScopeParamsSchema } from "../primitives/scope.js";
import { MAX_CLASS_AGE_LIMIT, MAX_STATION_CAPACITY, PRICING_MODES } from "../primitives/station-class.js";
import { commonProblemResponses, JSON_CONTENT_TYPE, problemResponse } from "../openapi/responses.js";
import { STATIONS_MANAGE, STATIONS_READ } from "../permissions/index.js";
import { PERMISSION_EXTENSION } from "./audit.js";
// The value limits themselves live in a browser-safe module the console can import without the route
// graph; re-exported here so `@clavian/contracts` still surfaces them and the schemas read one number.
export { MAX_CLASS_AGE_LIMIT, MAX_STATION_CAPACITY, PRICING_MODES } from "../primitives/station-class.js";
export const PricingModeSchema = z.enum(PRICING_MODES).openapi('PricingMode', {
    description: '`per_player`: each player on a station pays. `per_station`: the station is priced once, whoever plays.',
});
const nameSchema = z.string().trim().min(1).max(60);
const descriptionSchema = z
    .string()
    .trim()
    .max(500)
    .openapi({ description: 'Shown to players: what this class is and why it costs what it does.' });
const ageLimitSchema = z
    .int()
    .min(1)
    .max(MAX_CLASS_AGE_LIMIT)
    .nullable()
    .openapi({ description: 'The youngest a player may be to start a session here, on top of the venue’s minimum age.' });
const capacitySchema = z.int().min(1).max(MAX_STATION_CAPACITY);
export const StationClassSchema = z
    .object({
    id: StationClassIdSchema,
    organizationId: OrganizationIdSchema,
    venueId: VenueIdSchema,
    name: nameSchema,
    colorToken: ColorTokenSchema,
    description: z.string(),
    ageLimit: ageLimitSchema,
    pricingMode: PricingModeSchema,
    /** Players a station of this class seats unless the station says otherwise. */
    defaultCapacity: capacitySchema,
    createdAt: IsoDateTimeSchema,
    updatedAt: IsoDateTimeSchema,
})
    .openapi('StationClass', { description: 'A price and permission tier of stations (spec/04 §3).' });
export const StationClassListSchema = paginated(StationClassSchema, 'StationClassList');
export const CreateStationClassSchema = z
    .object({
    name: nameSchema,
    colorToken: ColorTokenSchema,
    description: descriptionSchema.optional(),
    ageLimit: ageLimitSchema.optional(),
    pricingMode: PricingModeSchema,
    defaultCapacity: capacitySchema.optional(),
})
    .strict()
    .openapi('CreateStationClass');
export const UpdateStationClassSchema = z
    .object({
    name: nameSchema,
    colorToken: ColorTokenSchema,
    description: descriptionSchema,
    ageLimit: ageLimitSchema,
    pricingMode: PricingModeSchema,
    defaultCapacity: capacitySchema,
})
    .partial()
    .strict()
    .refine((patch) => Object.keys(patch).length > 0, { message: 'Send at least one field to change.' })
    .openapi('UpdateStationClass');
const StationClassParamsSchema = z
    .object({ organizationId: OrganizationIdSchema, venueId: VenueIdSchema, classId: StationClassIdSchema })
    .openapi('StationClassParams');
const forbidden = problemResponse('Your role does not allow this.', 'forbidden', 'second-factor-required');
const venueNotFound = problemResponse('No such venue in this organisation, or not one you can see.', 'not-found');
const nameTaken = problemResponse('Another class at this venue already has that name.', 'conflict');
export const listStationClassesRoute = createRoute({
    method: 'get',
    path: '/v1/organizations/{organizationId}/venues/{venueId}/station-classes',
    operationId: 'listStationClasses',
    tags: ['stations'],
    summary: "List a venue's station classes",
    description: 'Oldest first. Requires `stations:read`.',
    [PERMISSION_EXTENSION]: STATIONS_READ,
    request: { params: VenueScopeParamsSchema, query: PageQuerySchema },
    responses: {
        200: { description: 'A page of classes.', content: { [JSON_CONTENT_TYPE]: { schema: StationClassListSchema } } },
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: forbidden,
        404: venueNotFound,
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const createStationClassRoute = createRoute({
    method: 'post',
    path: '/v1/organizations/{organizationId}/venues/{venueId}/station-classes',
    operationId: 'createStationClass',
    tags: ['stations'],
    summary: 'Create a station class',
    description: 'Requires `stations:manage`. Audited.',
    [PERMISSION_EXTENSION]: STATIONS_MANAGE,
    request: {
        params: VenueScopeParamsSchema,
        body: { content: { [JSON_CONTENT_TYPE]: { schema: CreateStationClassSchema } }, required: true },
    },
    responses: {
        201: { description: 'The class that was created.', content: { [JSON_CONTENT_TYPE]: { schema: StationClassSchema } } },
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: forbidden,
        404: venueNotFound,
        409: nameTaken,
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const updateStationClassRoute = createRoute({
    method: 'patch',
    path: '/v1/organizations/{organizationId}/venues/{venueId}/station-classes/{classId}',
    operationId: 'updateStationClass',
    tags: ['stations'],
    summary: 'Edit a station class',
    description: 'Only the fields sent change. Requires `stations:manage`. Audited with the before and after.',
    [PERMISSION_EXTENSION]: STATIONS_MANAGE,
    request: {
        params: StationClassParamsSchema,
        body: { content: { [JSON_CONTENT_TYPE]: { schema: UpdateStationClassSchema } }, required: true },
    },
    responses: {
        200: { description: 'The class as it is now.', content: { [JSON_CONTENT_TYPE]: { schema: StationClassSchema } } },
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: forbidden,
        404: problemResponse('No such class at this venue.', 'not-found'),
        409: nameTaken,
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
//# sourceMappingURL=station-classes.js.map